/**
 * TopBar
 *
 * Application header with skill name, actions, and status
 */

import { useState } from 'react';
import { useProjectStore, type StageType, type ProjectStageType, type ArtifactStageType } from '@/stores/projectStore';
import { useTheme } from '@/hooks/useTheme';
import { Button } from '../ui/Button';
import { Download, Save, Sun, Moon, Settings, RulerDimensionLine, Folder, Loader2, UserCircle2, LogOut, Shield, DraftingCompass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ExportProjectModal } from '../modals/ExportProjectModal';
import { useToastStore } from '@/stores/toastStore';
import { useUserStore } from '@/stores/userStore';

const STAGE_LABELS: Record<StageType, string> = {
  PLAN: 'Master Plan',
  LUMASCRIPT: 'LumaScript',
  BPMN: 'BPMN',
  ARTIFACT_DESIGN: 'Artifact Design',
  PROMPT: 'Prompt',
  ARTIFACT_PLAN: 'Artifact Plan',
  LUMA_SKILL: 'Skill Editor',
  LUMAJSON: 'LumaJSON'
};

type StageScope =
  | { scope: 'project'; stage: ProjectStageType }
  | { scope: 'artifact'; stage: ArtifactStageType }
  | { scope: 'none' };

export const TopBar: React.FC = () => {
  const getActiveProject = useProjectStore((state) => state.getActiveProject);
  const getSelectedArtifact = useProjectStore((state) => state.getSelectedArtifact);
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const activeStage = useProjectStore((state) => state.ui.activeStage);
  const loadProject = useProjectStore((state) => state.loadProject);
  const loadArtifacts = useProjectStore((state) => state.loadArtifacts);
  const updateProjectStage = useProjectStore((state) => state.updateProjectStage);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const resetProjects = useProjectStore((state) => state.resetStore);

  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { addToast } = useToastStore();

  const activeProject = getActiveProject();
  const selectedArtifact = getSelectedArtifact();
  const { users, activeUserId, selectUser, logout: logoutUser, createUser, updateUserRole } = useUserStore();
  const activeUser = users.find((user) => user.id === activeUserId) || null;
  const activeProjectDisplayName = activeProject?.isInbox ? 'Drafts' : activeProject?.name;

  const [showExportModal, setShowExportModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [switchingUserId, setSwitchingUserId] = useState<string | null>(null);
  const [promotingSelf, setPromotingSelf] = useState(false);

  const handleExportClick = () => {
    if (!activeProject || activeProject.name === 'Draftboard') {
      alert('Please select a project to export. Draftboard cannot be exported.');
      return;
    }

    setShowExportModal(true);
  };

  const handleSwitchUser = async (userId: string) => {
    if (userId === activeUserId) {
      setShowUserMenu(false);
      return;
    }
    setSwitchingUserId(userId);
    try {
      resetProjects();
      selectUser(userId);
    } finally {
      setSwitchingUserId(null);
      setShowUserMenu(false);
    }
  };

  const handleCreateUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newUserName.trim()) return;
    setCreatingUser(true);
    try {
      const user = await createUser(newUserName.trim());
      if (user) {
        resetProjects();
        selectUser(user.id);
      }
      setNewUserName('');
      setShowUserMenu(false);
    } catch (error) {
      console.error('[TopBar] Failed to create user', error);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleLogout = () => {
    resetProjects();
    logoutUser();
    setShowUserMenu(false);
  };

  const handleSelfPromote = async () => {
    if (!activeUser) return;
    setPromotingSelf(true);
    try {
      await updateUserRole(activeUser.id, 'admin');
      addToast('success', 'You now have admin access.');
    } catch (error) {
      console.error('[TopBar] Failed to promote self', error);
      addToast('error', 'Unable to update role. Try again.');
    } finally {
      setPromotingSelf(false);
    }
  };

  // Get selected artifact ID to pass to export modal
  const selectedArtifactId = selectedArtifact?.id || null;

  const resolveStageScope = (): StageScope => {
    if (!activeStage) {
      return { scope: 'none' };
    }

    if (activeStage === 'PLAN' || activeStage === 'ARTIFACT_DESIGN') {
      return { scope: 'project', stage: activeStage };
    }

    if (activeStage === 'PROMPT' || activeStage === 'ARTIFACT_PLAN' || activeStage === 'LUMA_SKILL' || activeStage === 'LUMAJSON') {
      return { scope: 'artifact', stage: activeStage };
    }

    if (activeStage === 'LUMASCRIPT' || activeStage === 'BPMN') {
      if (selectedArtifact) {
        return { scope: 'artifact', stage: activeStage };
      }
      return { scope: 'project', stage: activeStage };
    }

    return { scope: 'none' };
  };

  const stageScope = resolveStageScope();
  const activeStageData =
    stageScope.scope === 'project'
      ? activeProject?.stages?.[stageScope.stage]
      : stageScope.scope === 'artifact'
        ? selectedArtifact?.stages?.[stageScope.stage]
        : null;

  const canSave = Boolean(
    stageScope.scope !== 'none' &&
      activeStageData &&
      activeStageData.isDirty &&
      !isSaving &&
      activeProjectId
  );

  const stageLabel = activeStage ? STAGE_LABELS[activeStage] ?? activeStage : 'stage';
  const saveTooltip = (() => {
    if (!activeStage) {
      return 'Select a stage to save.';
    }
    if (!activeStageData) {
      return 'Nothing to save for this stage yet.';
    }
    if (!activeStageData.isDirty) {
      return `${stageLabel} has no unsaved changes.`;
    }
    return `Save ${stageLabel}`;
  })();

  const handleSaveClick = async () => {
    if (!activeStage || stageScope.scope === 'none') {
      addToast('warning', 'Select a stage to save first.');
      return;
    }

    if (!activeStageData) {
      addToast('warning', `Nothing to save for ${stageLabel}.`);
      return;
    }

    if (!activeStageData.isDirty) {
      addToast('info', `${stageLabel} is already saved.`);
      return;
    }

    if (!activeProject || !activeProjectId) {
      addToast('error', 'No project is loaded.');
      return;
    }

    setIsSaving(true);
    const payloadContent = typeof activeStageData.content === 'string' ? activeStageData.content : '';

    try {
      if (stageScope.scope === 'project') {
        await updateProjectStage(activeProject.id, stageScope.stage, payloadContent, false);
        try {
          await loadProject(activeProject.id);
        } catch (refreshError) {
          console.warn('[TopBar] Failed to refresh project after save', refreshError);
        }
      } else {
        if (!selectedArtifact) {
          addToast('error', 'Select an artifact to save this stage.');
          setIsSaving(false);
          return;
        }
        await updateArtifactStage(activeProject.id, selectedArtifact.id, stageScope.stage, payloadContent, false);
        try {
          await loadArtifacts(activeProject.id);
        } catch (refreshError) {
          console.warn('[TopBar] Failed to refresh artifacts after save', refreshError);
        }
      }

      addToast('success', `${stageLabel} saved.`);
    } catch (error) {
      console.error('[TopBar] Failed to save stage', error);
      addToast('error', `Failed to save ${stageLabel}.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="h-12 border-b border-border bg-background flex items-center justify-between px-4">
      {/* Left: Logo and Project/Artifact Name */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Serviceaide Logo" className="w-6 h-6" />
          <h1 className="text-lg font-semibold">Luma Skill Factory</h1>
        </div>
        {activeProject && (
          <>
            <div className="text-sm text-muted-foreground">|</div>
            <div className="flex items-center gap-1.5">
              {activeProject.name === 'Draftboard' ? (
                <RulerDimensionLine className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
              <span className="text-sm font-medium inline-flex items-center gap-1">
                {activeProject.isInbox && <DraftingCompass className="w-4 h-4" />}
                {activeProjectDisplayName}
              </span>
            </div>
          </>
        )}
        {selectedArtifact && (
          <>
            <div className="text-sm text-muted-foreground">/</div>
            <div className="text-sm text-muted-foreground">{selectedArtifact.name}</div>
          </>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSaveClick}
          disabled={!canSave}
          title={saveTooltip}
        >
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        <Button
          size="sm"
          onClick={handleExportClick}
          disabled={!activeProject || activeProject.name === 'Draftboard'}
          title={
            !activeProject
              ? 'Select a project to export'
              : activeProject.name === 'Draftboard'
                ? 'Draftboard cannot be exported'
                : `Export ${activeProjectDisplayName}`
          }
        >
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
        <Button size="sm" variant="ghost" onClick={() => navigate('/settings')} title="Settings">
          <Settings className="w-4 h-4" />
        </Button>
        <div className="relative">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowUserMenu((prev) => !prev)}
            title="Switch User"
            className="flex items-center gap-2"
          >
            <UserCircle2 className="w-4 h-4" />
            <span className="text-xs">{activeUser?.displayName || 'Select User'}</span>
          </Button>
          {showUserMenu && (
            <div
              className="absolute right-0 mt-2 w-64 bg-background border border-border rounded-md shadow-lg z-50 p-3 space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase">Active User</div>
                <div className="text-sm font-medium">{activeUser?.displayName || 'None selected'}</div>
                {activeUser && (
                  <div className="text-xs text-muted-foreground capitalize">{activeUser.role}</div>
                )}
              </div>
              {activeUser && activeUser.role !== 'admin' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full justify-start"
                  disabled={promotingSelf}
                  onClick={handleSelfPromote}
                >
                  {promotingSelf ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  Become Admin
                </Button>
              )}
              {users.filter((user) => user.id !== activeUserId).length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-semibold text-muted-foreground uppercase">Switch To</div>
                  {users
                    .filter((user) => user.id !== activeUserId)
                    .map((user) => (
                      <button
                        key={user.id}
                        className="w-full text-left text-sm px-2 py-1 rounded hover:bg-accent flex items-center justify-between"
                        onClick={() => handleSwitchUser(user.id)}
                        disabled={switchingUserId === user.id}
                      >
                        <span>{user.displayName}</span>
                        {switchingUserId === user.id && <Loader2 className="w-3 h-3 animate-spin" />}
                      </button>
                    ))}
                </div>
              )}
              <form onSubmit={handleCreateUser} className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase">Add User</label>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Display name"
                  className="w-full border border-border rounded px-3 py-1.5 text-sm bg-background"
                />
                <Button type="submit" size="sm" className="w-full" disabled={!newUserName.trim() || creatingUser}>
                  {creatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create & Switch'}
                </Button>
              </form>
              <Button
                size="sm"
                variant="ghost"
                className="w-full justify-start text-red-500 hover:bg-red-500/10"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Export Modal */}
      {showExportModal && activeProject && (
        <ExportProjectModal
          project={activeProject}
          selectedArtifactId={selectedArtifactId}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </>
  );
};
